import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import HomePageBanner from "../components/ads/HomePageBanner";
import { GiftIcon } from "@heroicons/react/solid";
import { useAuth } from "../contexts/AuthContext";
import Toast from "../components/Toast";

export default function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const desktopImages = [
    "https://geneartai.com/images/image1.png",
  ];
  const mobileImages = [
    "https://geneartai.com/images/mobile1.png",
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };



  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        (prev + 1) % (isMobile ? mobileImages.length : desktopImages.length)
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [isMobile]);

  const currentBackground = isMobile
    ? mobileImages[currentImageIndex]
    : desktopImages[currentImageIndex];

  return (
    <div className="relative min-h-screen font-sans text-white overflow-x-hidden flex flex-col">

      {/* ✅ Fixed Fullscreen Background */}
      <div className="fixed inset-0 z-0">
        <motion.img
          key={currentBackground}
          src={currentBackground}
          alt="Background"
          className="w-full h-full object-cover"
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ duration: 6 }}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        />
      </div>

      {/* ✅ Overlay */}
      <div className="absolute inset-0 " />

      {/* ✅ Foreground Content */}
      <div className="relative z-10 flex flex-col flex-grow justify-between">
        <div className="flex-grow flex flex-col justify-between">

          {/* Hero Section */}
          <div className="flex flex-col items-center justify-center text-center px-5 pt-24 pb-8 ">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="rounded-xl bg-black/30 backdrop-blur-sm p-5 flex flex-col items-center"
            >
              <h1 className="text-2xl sm:text-5xl md:text-4xl lg:text-6xl font-extrabold mb-6 leading-tight text-gray-100">
                Generate Stunning AI Art — 100% Free 🎨
              </h1>

              <p className="text-lg md:text-2xl max-w-3xl mb-8 text-gray-100 text-center mx-auto">
                GeneArtAI empowers everyone to create beautiful digital artwork instantly.
                No payments. No limits. Just pure creativity at your fingertips.
              </p>

              {/* Start Creating Button */}
              <motion.button
                onClick={() => navigate("/generate")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-800 hover:bg-[#166CB0] text-white font-bold py-2 px-8 rounded-full transition-all duration-300 shadow-md mb-3"
              >
                Start Creating for Free
              </motion.button>

              {/* 20 Free Credits Banner */}
              {!currentUser && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  onClick={() => {
                    if (!currentUser) navigate("/signup");
                  }}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold text-xs sm:text-sm md:text-base px-4 py-2 rounded-full shadow-md hover:scale-105 transform transition duration-300 animate-bounce cursor-pointer"
                >
                  <GiftIcon className="h-5 w-5 text-yellow-900" />
                  20 Credits Instantly When You Sign Up — Limited Time!
                </motion.div>
              )}
            </motion.div>
          </div>



          {/* Banner Ad
          <div className="hidden md:block">
            <HomePageBanner />
          </div> */}

          {/* Features */}
          <section className="p-10">
            <div className="max-w-8xl mx-auto w-full rounded-2xl p-5 flex flex-col items-center text-center bg-black/25 backdrop-blur-sm">
              <motion.h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-100 mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Why Choose GeneArtAI?
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 w-full">
                {[
                  {
                    title: "🆓 Free Forever",
                    desc: "Generate unlimited AI images without paying a cent — no hidden charges.",
                  },
                  {
                    title: "⚡ Fast & Easy",
                    desc: "Just type a prompt, click a button, and see magic happen in seconds.",
                  },
                  {
                    title: "🔒 Private & Secure",
                    desc: "Your prompts and generated artwork are private and protected.",
                  },
                ].map((feature, idx) => (
                  <motion.div
                    key={idx}
                    className="p-4"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.2 }}
                  >
                    <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-4 text-white">
                      {feature.title}
                    </h3>
                    <p className="text-gray-300">{feature.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
          {/* SEO-friendly Text Block */}
          <section className="p-6 md:p-10 bg-black/30 backdrop-blur-md rounded-xl mx-auto max-w-5xl text-white text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Your Imagination, Powered by AI
            </h2>
            <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
              GeneArtAI is your ultimate creative companion, enabling artists, designers, and everyday dreamers to bring their ideas to life using the power of artificial intelligence. Whether you're looking to create stunning visuals for a project, explore futuristic art styles, or simply have fun generating one-of-a-kind images, our platform makes it effortless and accessible.
              <br /><br />
              With an intuitive interface and no design experience required, GeneArtAI allows anyone to type a prompt and generate high-quality artwork instantly. From fantasy landscapes and digital portraits to surreal mashups and product mockups, the only limit is your imagination.
              <br /><br />
              We believe creativity should be free. That’s why we offer 20 free credits on signup — with no hidden fees, no subscriptions, and no watermarking. You can also earn additional credits simply by watching quick ads.
              <br /><br />
              Designed for speed, privacy, and creativity, GeneArtAI is the perfect blend of cutting-edge technology and human inspiration. Join thousands of creators already exploring what’s possible when AI meets art.
            </p>
          </section>


          {/* Footer */}
          <footer className="text-center text-xs text-gray-300 pb-3 mt-auto">
            <div>© 2025 GeneArtAI — All Rights Reserved.</div>
            <div className="mt-2">
              <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
                Privacy Policy
              </Link>{" "}
              |{" "}
              <Link to="/terms-of-service" className="underline hover:text-white">
                Terms of Service
              </Link>
            </div>
          </footer>

          {/* Spacer for bottom ad */}
          <div className="block sm:hidden h-20" />
        </div>
      </div>
    </div>
  );
}
